import React, { useRef } from 'react';
import cx from 'classnames';
import styles from './Counter.module.css';

type Props = {
  id: string;
  name: string;
  value: number;
  onChange: (value: any) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
};

const HOME_KEY = 36;
const END_KEY = 35;

// a11y: https://www.w3.org/TR/wai-aria-practices-1.1/#spinbutton
function Counter({
  id,
  name,
  value,
  onChange,
  min = 0,
  max = Infinity,
  disabled
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (val: number) => {
    const newValue = Math.max(min, Math.min(max, val || 0));
    return onChange({
      target: {
        name,
        value: newValue
      }
    });
  };

  const handleKeyDown = (e: any) => {
    const keyCode = e.which;
    if (keyCode === HOME_KEY) {
      e.preventDefault();
      handleChange(min);
    } else if (keyCode === END_KEY && max < Infinity) {
      e.preventDefault();
      handleChange(max);
    }
  };

  return (
    <div
      className={styles.root}
      aria-valuenow={value}
      aria-valuemin={min}
      aria-valuemax={max === Infinity ? undefined : max}
      tabIndex={-1}
    >
      <button
        type="button"
        onClick={() => {
          handleChange(value - 1);
          inputRef.current!.focus();
        }}
        className={cx(styles.button)}
        disabled={disabled || value <= min}
        title="Færre"
        tabIndex={-1}
      >
        -
      </button>
      <input
        ref={inputRef}
        id={id}
        role="spinbutton"
        type="number"
        className={cx(styles.value, styles.numberInput)}
        min={min}
        max={max}
        value={value}
        onKeyDown={handleKeyDown}
        onChange={e => handleChange(parseInt(e.target.value, 10))}
        disabled={disabled}
      />
      <button
        type="button"
        onClick={() => {
          handleChange(value + 1);
          inputRef.current!.focus();
        }}
        className={styles.button}
        disabled={disabled || value >= max}
        title="Flere"
        tabIndex={-1}
      >
        +
      </button>
    </div>
  );
}

export default Counter;
